import { useEffect, useState } from 'react'
import { X } from 'lucide-react'
import { Spinner, Tip } from '@/components/ui'
import { listAssets } from '@/services/assets'
import { matchesAssetSearch } from '@/utils/assetSearch'
import type { ApprovedAsset } from '@/types/assets'

interface Props {
  open: boolean
  onClose: () => void
  onPick: (mediaRef: string) => void
}

export function AssetLibraryPicker({ open, onClose, onPick }: Props) {
  const [assets, setAssets] = useState<ApprovedAsset[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')

  useEffect(() => {
    if (!open) return
    let cancelled = false
    setLoading(true)
    setSearch('')
    listAssets()
      .then((a) => { if (cancelled) return; setAssets(a.filter((x) => x.isActive && x.kind === 'file')); setLoading(false) })
      .catch(() => { if (cancelled) return; setAssets([]); setLoading(false) })
    return () => { cancelled = true }
  }, [open])

  const visible = assets.filter((a) => matchesAssetSearch(a, search))

  if (!open) return null
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div
        className="bg-card rounded-lg p-4 w-96 max-h-[70vh] overflow-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold">Send from library</h3>
          <Tip text="Close"><button aria-label="Close" onClick={onClose}><X className="w-4 h-4" /></button></Tip>
        </div>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name, slug, or tag"
          className="w-full mb-3 px-3 py-1.5 text-sm rounded-sm border border-border bg-card"
        />
        {loading ? (
          <Spinner size="sm" />
        ) : visible.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            {assets.length === 0 ? 'No approved files yet.' : 'No files match your search.'}
          </p>
        ) : (
          <ul className="space-y-1">
            {visible.map((a) => (
              <li key={a.id}>
                <button
                  className="w-full text-left px-2 py-1.5 rounded-sm hover:bg-accent text-sm"
                  onClick={() => { onPick(a.mediaRef); onClose() }}
                >
                  {a.name} <span className="text-xs text-muted-foreground">({a.slug})</span>
                  {a.tags.length > 0 && (
                    <span className="flex flex-wrap gap-1 mt-0.5">
                      {a.tags.map((t) => (
                        <span key={t} className="px-1.5 py-0.5 text-[10px] rounded-full bg-primary/10 text-primary">
                          {t}
                        </span>
                      ))}
                    </span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}
